import { Component, ElementRef, effect, inject, input, signal, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { AuthService } from '../../../../services/auth.service';

@Component({
  selector: 'app-profile-menu',
  standalone: true,
  imports: [RouterLink, TranslocoPipe],
  templateUrl: './profile-menu.component.html',
  styleUrl: './profile-menu.component.scss',
})
export class ProfileMenuComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly triggerBtn = viewChild<ElementRef<HTMLButtonElement>>('triggerBtn');

  readonly initial = input.required<string>();
  readonly open = signal(false);

  constructor() {
    // Focus the first menu item when the menu opens so keyboard users can immediately
    // navigate with arrow keys without needing an extra Tab press.
    effect(() => {
      if (this.open()) {
        setTimeout(() => this.getMenuItems()[0]?.focus(), 0);
      }
    });
  }

  toggle(): void {
    this.open.set(!this.open());
  }

  close(): void {
    this.open.set(false);
  }

  async onSignOut(): Promise<void> {
    this.close();
    await this.authService.signOut().catch(() => undefined);
    await this.router.navigate(['/auth']);
  }

  onMenuKeyDown(event: KeyboardEvent): void {
    const items = this.getMenuItems();
    const current = items.findIndex(el => el === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        items[(current + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        items[(current - 1 + items.length) % items.length]?.focus();
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this.triggerBtn()?.nativeElement.focus();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  private getMenuItems(): HTMLElement[] {
    return Array.from(
      (this.elementRef.nativeElement as HTMLElement).querySelectorAll<HTMLElement>('[role="menuitem"]')
    );
  }
}
